import { Application } from '@tencent/alloy-renderer';
import { BrowserTarget } from '@tencent/alloy-renderer-browser';
import { NoteArea, NoteAreaProps } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/note/note-area';
import type { ForceStyle } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-mobile/note/common-note-data';
import { NOTE_COLLAPSE_HEIGHT } from '@editor/src/js/constants';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';

declare global {
    interface Window {
        noteApp: any;
    }
}

type NoteLayerServiceProps = Omit<NoteAreaProps, 'application'> & {
    width: number,
    height: number,
};

export class NoteLayerService {
    private readonly application: Application<BrowserTarget>;
    private noteArea: NoteArea;

    public constructor(props: NoteLayerServiceProps) {
        const { canvasElement, width, height } = props;
        this.application = new Application(new BrowserTarget(), {
            interactive: true,
            native: canvasElement,
            delayDrawHit: false,
        });
        this.application.width = width;
        this.application.height = height;
        this.application.resolution = Math.ceil(window.devicePixelRatio);
        if (__IS_DEV__ || ENV.IS_TEST) {  // 测试环境挂载到window
            window.noteApp = this.application;
        }
        this.buildLayer({
            ...props,
            application: this.application,
        });
    }

    public updateWithData(width: number, height: number, forceStyle?: ForceStyle): void {
        // 外部容器传递的宽高为dom元素的像素值，在浏览器缩放时，会有小数点问题，需要ceil
        width = Math.ceil(width);
        height = Math.ceil(height);
        // 隐藏备注时，高度为8，此时内部 canvas 置为0避免打开备注时，短暂出现被截断的文字
        if (this.isHideNote(height)) {
            height = 0;
        }
        if (this.application.width !== width) {
            this.application.requestRender();
            if (this.noteArea) {
                this.noteArea.saveContainerWidth(width);
                this.noteArea.widthHasChanged = true;
                this.noteArea.resetScrollState();
                this.noteArea.updateEditorProps();
            }
        }
        if (this.application.height !== height) {
            this.noteArea?.saveContainerHeight(height); // containerHeight 的保存必须在 updateNoteHeight 之前
            this.noteArea?.updateNoteHeight();
        }

        this.noteArea?.updateForceStyle(forceStyle);
    }

    // TODO: dispose 后 noteArea 可能仍被某处持有，会有内存泄露问题
    public dispose(): void {
        this.noteArea.dispose();
        this.application.stage.removeChild(this.noteArea);
    }

    private buildLayer(noteAreaProps: NoteAreaProps) {
        this.noteArea = new NoteArea(noteAreaProps);
        this.application.stage.addChild(this.noteArea);
        this.application.requestRender();
    }

    private isHideNote = (height: number): boolean => height <= NOTE_COLLAPSE_HEIGHT;
}
